/** Watchlist hover copy — two-line explanations. */

export const CONFLUENCE_TIPS = {
  "BREAK ⚡": "Donchian Breakout — price closed above the highest high of the last 20 sessions on above-average volume. Institutional buying confirmed.",
  "FIB 🎯": "Fibonacci Pullback — price has retraced to a key fib level (38.2%, 50% or 61.8%) while structure and accumulation remain intact. Classic entry zone.",
  "BOUNCE ↗": "Fib Bounce — price touched a fib support level and closed back above it, confirmed by RSI bullish divergence and rising OBV. Reversal in progress.",
  "DIV ⚠": "RSI Divergence Warning — price made a higher high but momentum (RSI) made a lower high. Suggests the move may be losing conviction. Not an exit signal — watch for volume confirmation.",
  "—": "No tier-1 confluence setup is active on this scan.",
};

export const SCORE_TIP =
  "Composite score (-15 to +15) based on 4 tiers: Structure (EMA), Volume & Flow, Fibonacci position, and Momentum. Market Tide and Macro adjustments applied after. Above +6 = consider calls. Below -6 = consider puts.";

export const CMF_TIP =
  "Chaikin Money Flow — measures buying vs selling pressure over 20 days. Above +0.10 = accumulation. Below -0.10 = distribution. The most reliable volume indicator for swing trading.";

export const RS_SPY_TIP =
  "Relative Strength vs S&P 500. RS Rating 1-99 (higher = stronger). ⭐ = RS Line at new high — stocks that outperform before the market moves are the best setups.";

/** Bollinger band width near 6-month low — not a directional “squeeze up”. */
export const BB_TIGHT_BANDS_LABEL = "Tight bands";
export const BB_TIGHT_BANDS_SHORT = "BB tight";
export const BB_TIGHT_BANDS_TIP =
  "Bollinger Band width is near its 6-month low — volatility is compressed. Not bullish or bearish by itself; wait for price to break a band with volume before acting.";

const VERDICT_TIPS = [
  { match: (v) => /CONSIDER CALLS|BUY NOW|POWER PLAY|STANDARD LONG/i.test(v), tip: "Strong setup — structure, volume and momentum are aligned. Score above threshold for a high-conviction entry. Confirm with your risk parameters." },
  { match: (v) => /WATCH|👁/i.test(v) && !/EXTENDED/i.test(v), tip: "Setup building — not all conditions met yet. Monitor for volume confirmation or fib level touch before entering." },
  { match: (v) => /WAIT — EXTENDED|EXTENDED/i.test(v), tip: "Move already happened — price has run significantly from base. Wait for pullback to EMA21 or fib level before considering entry. Chasing here is high risk." },
  { match: (v) => /BOTTOM CONFIRMED|🔵/i.test(v), tip: "Reversal pattern detected — double bottom or higher low structure with volume exhaustion at the lows. Wait for continuation confirmation before full position." },
  { match: (v) => /TOP FORMING|⚠ TOP/i.test(v), tip: "Distribution detected — price showing weakness at highs with declining volume and/or RSI divergence. Avoid new longs. Consider reducing position." },
  { match: (v) => /WAIT|⚪|HOLD|CHOP/i.test(v), tip: "No edge — signals are mixed or neutral. Stay in cash until a cleaner setup develops." },
];

export function signalVerdictTooltip(verdict) {
  if (!verdict) return undefined;
  for (const { match, tip } of VERDICT_TIPS) {
    if (match(verdict)) return tip;
  }
  return VERDICT_TIPS.find((x) => x.match("WAIT")).tip;
}

export function rsiCellTooltip(rsi, rsiDiv) {
  if (rsi == null) return undefined;
  if (rsiDiv?.bullish || rsiDiv?.rsiBullishDivergence) {
    return "RSI Bullish Divergence — price made a lower low but RSI made a higher low. Selling pressure is exhausting. Potential reversal signal.";
  }
  if (rsiDiv?.bearish || rsiDiv?.rsiBearishDivergence) {
    return "RSI Bearish Divergence — price made a higher high but RSI made a lower high. Momentum is fading. Watch for distribution signals to confirm.";
  }
  if (rsi > 70) {
    return "Strong Momentum — RSI above 70 indicates institutional buying pressure. Trending stocks can hold this level for weeks. Not an exit signal.";
  }
  if (rsi >= 50) {
    return "Bullish momentum zone — RSI above 50 confirms uptrend is intact.";
  }
  return "Caution — RSI below 50 suggests momentum has shifted bearish.";
}

export function confluenceCellTooltip(cf) {
  if (!cf?.text) return CONFLUENCE_TIPS["—"];
  return CONFLUENCE_TIPS[cf.text] || cf.tip || CONFLUENCE_TIPS["—"];
}

/** Short bridge line under Signal verdict when confluence is active. */
export const CONFLUENCE_SIGNAL_BRIDGE = {
  "BREAK ⚡": "Donchian breakout — confluence aligns with score",
  "FIB 🎯": "Fib pullback zone — tier-1 confluence active",
  "BOUNCE ↗": "Fib bounce — reversal confluence active",
  "DIV ⚠": "RSI distribution warning — watch volume",
};

export function confluenceSignalBridge(result, watchlistConfluenceCellFn) {
  if (!result || !watchlistConfluenceCellFn) return null;
  const cf = watchlistConfluenceCellFn(result);
  if (!cf?.text || cf.text === "—") return null;
  return CONFLUENCE_SIGNAL_BRIDGE[cf.text] || null;
}

export const SIGNAL_GUIDE_INTRO = [
  "Auctus reads each ticker in four layers — structure (EMA stack and trend), volume and institutional flow (CMF, OBV, RVOL), Fibonacci position (pullback vs extension), and momentum (RSI midline and context-aware divergence). Those layers roll into a composite score from −15 to +15, then Market Tide and macro phase adjustments are applied.",
  "Confluence is the highest-conviction overlay: when fib pullback, Donchian breakout, bounce, or distribution templates align with their full checklists, you see a single badge on the watchlist. The verdict banner combines score, entry gate, and extension rules into one action-oriented label — score alone never equals “buy now.”",
  "Hover any watchlist cell for a short definition. Open a ticker drawer → Analysis tab for tier score breakdown, module checklists, and price story. Use Tradeflow for signal timeline and flow confirmation.",
];

export const SIGNAL_GUIDE_VERDICTS = [
  { label: "⚡ Consider Calls / Strong long tiers", body: "Structure, volume, and momentum align; score supports a high-conviction long. Confirm gate GO and your risk size." },
  { label: "👁 Watch", body: "Setup is forming — wait for volume, fib touch, or gate confirmation." },
  { label: "⚪ Wait", body: "Mixed or neutral evidence — no clear edge." },
  { label: "⚠ Wait — Extended", body: "Price has already extended; wait for pullback to EMA21 or fib support." },
  { label: "🔵 Bottom Confirmed", body: "Reversal structure at lows; wait for continuation before sizing up." },
  { label: "⚠ Top Forming", body: "Weakness at highs — avoid new longs; watch distribution and RSI context divergence." },
];

export const SIGNAL_GUIDE_CONFLUENCE = Object.entries(CONFLUENCE_TIPS)
  .filter(([k]) => k !== "—")
  .map(([label, body]) => ({ label, body }));

export const SIGNAL_GUIDE_TIERS = [
  { label: "Tier 1 — Structure", body: "Price vs EMA50/EMA200 stack; golden structure adds the most points." },
  { label: "Tier 2 — Volume & Flow", body: "Volume expansion, CMF, OBV — institutional participation." },
  { label: "Tier 3 — Fibonacci", body: "Proximity to 38.2–61.8% retracement and active bounce." },
  { label: "Tier 4 — Donchian", body: "Position vs 20-day channel midline and breakout status." },
  { label: "Tier 5 — Momentum", body: "RSI midline and filtered divergence only when context rules pass." },
  { label: "Confluence bonus", body: "+2 to +3 when a full tier-1 template fires." },
];

export const SIGNAL_GUIDE_COLUMNS = [
  { label: "Signal", body: "Verdict + strength — synthesized action label." },
  { label: "Action", body: "Options phase (WATCH → SETUP → READY) and setup type — not a duplicate verdict." },
  { label: "Score", body: SCORE_TIP },
  { label: "RSI", body: "Midline coloring; ↑/↓ only when context-aware divergence is active." },
  { label: "Confluence", body: "Top tier-1 setup badge or —." },
  { label: "CMF + Vol", body: CMF_TIP },
  { label: "RS vs SPY", body: RS_SPY_TIP },
];

export const ACTION_COLUMN_TIP =
  "Options phase and setup type — WATCH / SETUP / READY for calls or puts, plus active pattern (e.g. bull flag). The Signal column holds the verdict; Action is timing and structure.";
